// src/pages/PantheonPage.js
//
// CPHERE PANTHEON — Public Interactive Experience
//
// A premium, futuristic, public-facing storytelling page that introduces
// Cphere Pantheon: the intelligence network of Cphere's Unified Agentic Ad
// Infrastructure. This is NOT an admin tool, NOT the Pantheon Observatory,
// and NOT an internal operations console. It has no backend dependency and
// requires no authentication.
//
// Aesthetic: Apple + OpenAI + Anduril + Palantir + sci-fi infrastructure.
// The existing Cphere low-poly brain remains the centerpiece. No gods, no
// statues, no fantasy artwork.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import "./PantheonPage.css";

// The canonical Cphere low-poly brain (shipped in /public). Referenced via the
// public URL so we never re-draw or re-skin the brand asset.
const BRAIN_SRC = `${process.env.PUBLIC_URL || ""}/icon-512new.png`;

// --------------------------------------------------------------------------
// Static content (no backend, no Pantheon APIs)
// --------------------------------------------------------------------------

const AGENTS = [
  {
    id: "athena",
    name: "Athena",
    color: "#8B5CF6", // Violet
    role: "Strategy Intelligence",
    capabilities: ["Planning", "Decision Architecture", "Strategic Recommendations"],
  },
  {
    id: "apollo",
    name: "Apollo",
    color: "#3B82F6", // Blue
    role: "Forecasting Intelligence",
    capabilities: ["Prediction", "Signal Analysis", "Outcome Forecasting"],
  },
  {
    id: "artemis",
    name: "Artemis",
    color: "#22C55E", // Green
    role: "Audience Intelligence",
    capabilities: ["Audience Discovery", "Segmentation", "Targeting Insights"],
  },
  {
    id: "nike",
    name: "Nike",
    color: "#F97316", // Orange
    role: "Optimization Intelligence",
    capabilities: ["Performance Analysis", "Budget Intelligence", "Efficiency Detection"],
  },
  {
    id: "zeus",
    name: "Zeus",
    color: "#EF4444", // Red
    role: "Governance Authority",
    capabilities: ["Policy Evaluation", "Risk Analysis", "Trust Validation"],
  },
  {
    id: "hermes",
    name: "Hermes",
    color: "#F5C542", // Gold
    role: "Execution Gateway",
    capabilities: ["Deployment Coordination", "Workflow Orchestration"],
  },
  {
    id: "hephaestus",
    name: "Hephaestus",
    color: "#EC4899", // Pink
    role: "Build Intelligence",
    capabilities: ["Campaign Construction", "Plan Preparation", "Intent Translation"],
  },
  {
    id: "poseidon",
    name: "Poseidon",
    color: "#06B6D4", // Cyan
    role: "Cross Platform Orchestration",
    capabilities: ["Unified Channel Intelligence", "Platform Connectivity"],
  },
  {
    id: "themis",
    name: "Themis",
    color: "#14B8A6", // Teal
    role: "Verification Layer",
    capabilities: ["Validation", "Compliance", "Safety"],
  },
  {
    id: "hades",
    name: "Hades",
    color: "#6366F1", // Indigo
    role: "Memory Vault",
    capabilities: ["Audit", "Historical Intelligence", "Decision Memory"],
  },
  {
    id: "hestia",
    name: "Hestia",
    color: "#F59E0B", // Amber
    role: "Readiness Certification",
    capabilities: ["Readiness", "Qualification", "Operational Validation"],
  },
];

// Single source of truth for the public Pantheon agent set. Consumed by tests
// to guard against agent-count drift between the hero orbit, the interactive
// network, and the Observatory.
export const PANTHEON_AGENTS = AGENTS;

// Spell the agent count so the hero headline always tracks the AGENTS source of
// truth — no hard-coded number to drift out of sync (e.g. "Eleven").
const COUNT_WORDS = [
  "Zero", "One", "Two", "Three", "Four", "Five", "Six",
  "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
];
const AGENT_COUNT_WORD = COUNT_WORDS[AGENTS.length] || String(AGENTS.length);

const GOVERNANCE_LAYERS = [
  { id: "identity", label: "Identity", blurb: "Every intelligence is named, scoped, and known before it can reason." },
  { id: "authority", label: "Authority", blurb: "Authority is granted explicitly — never assumed." },
  { id: "reasoning", label: "Reasoning", blurb: "Decisions are produced with traceable, inspectable logic." },
  { id: "trust", label: "Trust", blurb: "Trust is measured continuously from evidence, not declared." },
  { id: "certification", label: "Certification", blurb: "Capabilities are certified before they are used." },
  { id: "flight", label: "Flight Certification", blurb: "Operational readiness is proven before live flight." },
  { id: "trials", label: "Authority Trials", blurb: "Authority is earned through supervised trials." },
  { id: "autonomy", label: "Future Autonomy", blurb: "Autonomy is the outcome of governance — never the starting point." },
];

const INFRASTRUCTURE = [
  { id: "strategy", label: "Strategy", agent: "Athena", note: "Strategic direction and decision architecture." },
  { id: "planning", label: "Planning", agent: "Athena", note: "Media plans translated into structured intent." },
  { id: "audience", label: "Audience Intelligence", agent: "Artemis", note: "Discovery, segmentation, and targeting." },
  { id: "creative", label: "Creative Eligibility", agent: "Themis", note: "Validation of creative against policy and platform." },
  { id: "budget", label: "Budget Allocation", agent: "Nike", note: "Efficient allocation across channels." },
  { id: "build", label: "Campaign Build", agent: "Hephaestus", note: "Campaign structures forged from strategy, intent, and platform constraints." },
  { id: "readiness", label: "Execution Readiness", agent: "Hestia", note: "Operational qualification before deployment." },
  { id: "deployment", label: "Platform Deployment", agent: "Hermes", note: "Coordinated deployment across platforms." },
  { id: "optimization", label: "Optimization", agent: "Nike", note: "Continuous performance and efficiency tuning." },
  { id: "verification", label: "Verification", agent: "Themis", note: "Compliance and safety verification." },
  { id: "audit", label: "Audit & Memory", agent: "Hades", note: "Durable decision memory and audit trail." },
];

// Map each agent name to its canonical Pantheon color so infrastructure cards
// can subtly echo the intelligence they belong to (single source of truth: the
// AGENTS palette above).
const AGENT_COLOR_BY_NAME = AGENTS.reduce((acc, agent) => {
  acc[agent.name] = agent.color;
  return acc;
}, {});

// --------------------------------------------------------------------------
// Reveal-on-scroll hook (gracefully degrades when IntersectionObserver is
// unavailable — e.g. jsdom test environment — by revealing immediately).
// --------------------------------------------------------------------------

function useReveal() {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, revealed];
}

function Section({ id, testId, className = "", children }) {
  const [ref, revealed] = useReveal();
  return (
    <section
      id={id}
      data-testid={testId}
      ref={ref}
      className={`pantheon-section ${revealed ? "is-revealed" : ""} ${className}`.trim()}
    >
      {children}
    </section>
  );
}

// --------------------------------------------------------------------------
// Interactive agent network (Section 3)
// --------------------------------------------------------------------------

function AgentNetwork({ selectedId, onSelect }) {
  // Lay the agents out evenly around the central brain. Positions are pure math
  // so there are no image/asset dependencies for the orbit itself.
  const nodes = useMemo(() => {
    const count = AGENTS.length;
    return AGENTS.map((agent, index) => {
      const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
      const radius = 42; // percent of the orbit box
      const x = 50 + Math.cos(angle) * radius;
      const y = 50 + Math.sin(angle) * radius;
      return { ...agent, x, y, angle };
    });
  }, []);

  return (
    <div className="pantheon-network" data-testid="pantheon-agent-network">
      <svg className="pantheon-network-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {nodes.map((node) => (
          <line
            key={`line-${node.id}`}
            x1="50"
            y1="50"
            x2={node.x}
            y2={node.y}
            className={`pantheon-line ${selectedId === node.id ? "is-active" : ""}`}
            style={{ stroke: node.color }}
          />
        ))}
      </svg>

      <div className="pantheon-network-core" aria-hidden="true">
        <img src={BRAIN_SRC} alt="" className="pantheon-network-brain" />
        <span className="pantheon-core-ring" />
        <span className="pantheon-core-ring pantheon-core-ring--slow" />
      </div>

      {nodes.map((node) => (
        <button
          key={node.id}
          type="button"
          data-testid={`agent-node-${node.id}`}
          className={`pantheon-node ${selectedId === node.id ? "is-selected" : ""}`}
          style={{
            left: `${node.x}%`,
            top: `${node.y}%`,
            "--agent-color": node.color,
          }}
          onClick={() => onSelect(node.id)}
          aria-pressed={selectedId === node.id}
          aria-label={`${node.name} — ${node.role}`}
        >
          <span className="pantheon-node-dot" />
          <span className="pantheon-node-label">{node.name}</span>
        </button>
      ))}
    </div>
  );
}

function AgentDetail({ agent }) {
  if (!agent) return null;
  return (
    <div
      className="pantheon-agent-detail"
      data-testid="pantheon-agent-detail"
      style={{ "--agent-color": agent.color }}
      role="region"
      aria-live="polite"
      aria-label={`${agent.name} detail`}
    >
      <div className="pantheon-agent-detail-head">
        <span className="pantheon-agent-glyph" />
        <div>
          <h3 className="pantheon-agent-name">{agent.name}</h3>
          <p className="pantheon-agent-role">{agent.role}</p>
        </div>
      </div>
      <ul className="pantheon-agent-caps">
        {agent.capabilities.map((cap) => (
          <li key={cap}>{cap}</li>
        ))}
      </ul>
    </div>
  );
}

// --------------------------------------------------------------------------
// Page
// --------------------------------------------------------------------------

export default function PantheonPage() {
  const [selectedId, setSelectedId] = useState("athena");

  const selectedAgent = useMemo(
    () => AGENTS.find((a) => a.id === selectedId) || null,
    [selectedId]
  );

  // Document-level treatment so the public experience renders full-bleed,
  // dark, and scrollable regardless of the app shell it is mounted in.
  useEffect(() => {
    const prevRoot = document.documentElement.getAttribute("data-route");
    document.documentElement.setAttribute("data-route", "/pantheon");
    document.body.setAttribute("data-route", "/pantheon");
    return () => {
      if (prevRoot) document.documentElement.setAttribute("data-route", prevRoot);
      else document.documentElement.removeAttribute("data-route");
      document.body.removeAttribute("data-route");
    };
  }, []);

  const scrollToId = useCallback((id) => {
    if (typeof document === "undefined") return;
    const el = document.getElementById(id);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return (
    <main className="pantheon-page" data-testid="pantheon-page">
      <div className="pantheon-bg" aria-hidden="true">
        <span className="pantheon-bg-grid" />
        <span className="pantheon-bg-glow pantheon-bg-glow--violet" />
        <span className="pantheon-bg-glow pantheon-bg-glow--blue" />
      </div>

      {/* Section 1 — Hero */}
      <header className="pantheon-hero" data-testid="pantheon-hero">
        <div className="pantheon-hero-logo">
          <a
            className="pantheon-hero-logo-link"
            href="https://analytics.cphere.ai"
            aria-label="Return to Cphere Analytics"
          >
            <CphereHeaderLogo alt="Cphere" />
          </a>
        </div>

        <div className="pantheon-hero-visual" aria-hidden="true">
          <div className="pantheon-hero-orbit">
            <span className="pantheon-hero-ring" />
            <span className="pantheon-hero-ring pantheon-hero-ring--2" />
            <span className="pantheon-hero-ring pantheon-hero-ring--3" />
            {AGENTS.map((agent, i) => (
              <span
                key={agent.id}
                className="pantheon-hero-node"
                style={{
                  "--agent-color": agent.color,
                  "--i": i,
                  "--total": AGENTS.length,
                }}
              />
            ))}
          </div>
          <img src={BRAIN_SRC} alt="" className="pantheon-hero-brain" />
        </div>

        <h1 className="pantheon-hero-title">PANTHEON</h1>
        <p className="pantheon-hero-subtitle">
          THE INTELLIGENCE NETWORK OF CPHERE&apos;S UNIFIED AGENTIC AD INFRASTRUCTURE
        </p>
        <button
          type="button"
          className="pantheon-cta pantheon-cta--primary"
          data-testid="pantheon-explore-cta"
          onClick={() => scrollToId("pantheon-what")}
        >
          Explore the Pantheon
        </button>

        <span className="pantheon-scroll-hint" aria-hidden="true" />
      </header>

      {/* Section 2 — What Is The Pantheon? */}
      <Section id="pantheon-what" testId="pantheon-what" className="pantheon-section--narrow">
        <p className="pantheon-eyebrow">The Pantheon</p>
        <h2 className="pantheon-h2">A network of governed intelligences</h2>
        <p className="pantheon-lead">
          The Pantheon is a network of specialized agent intelligence systems. Each
          agent focuses on a single, well-defined responsibility — and together they
          form one coordinated mind.
        </p>
        <div className="pantheon-pillars">
          {[
            "Strategy",
            "Forecasting",
            "Audience Intelligence",
            "Optimization",
            "Governance",
            "Verification",
            "Memory",
            "Orchestration",
          ].map((pillar) => (
            <span key={pillar} className="pantheon-pillar">
              {pillar}
            </span>
          ))}
        </div>
      </Section>

      {/* Section 3 — Interactive Agent Network */}
      <Section id="pantheon-agents" testId="pantheon-agents">
        <p className="pantheon-eyebrow">The Network</p>
        <h2 className="pantheon-h2">{`${AGENT_COUNT_WORD} intelligences. One brain.`}</h2>
        <p className="pantheon-lead pantheon-lead--center">
          Select an intelligence to explore its responsibility within the Pantheon.
        </p>

        <div className="pantheon-network-layout">
          <AgentNetwork selectedId={selectedId} onSelect={setSelectedId} />
          <AgentDetail agent={selectedAgent} />
        </div>
      </Section>

      {/* Section 4 — Governance Layers */}
      <Section id="pantheon-governance" testId="pantheon-governance">
        <p className="pantheon-eyebrow">Governance</p>
        <h2 className="pantheon-h2">Authority is built, layer by layer</h2>
        <p className="pantheon-lead pantheon-lead--center">
          Evidence before authority. Trust before action. Certification before trial.
        </p>

        <ol className="pantheon-flow">
          {GOVERNANCE_LAYERS.map((layer, i) => (
            <li
              key={layer.id}
              className="pantheon-flow-step"
              style={{ "--step": i }}
              data-testid={`governance-layer-${layer.id}`}
            >
              <span className="pantheon-flow-index">{String(i + 1).padStart(2, "0")}</span>
              <span className="pantheon-flow-label">{layer.label}</span>
              <span className="pantheon-flow-blurb">{layer.blurb}</span>
            </li>
          ))}
        </ol>
      </Section>

      {/* Section 5 — Unified Agentic Ad Infrastructure */}
      <Section id="pantheon-infrastructure" testId="pantheon-infrastructure">
        <p className="pantheon-eyebrow">Infrastructure</p>
        <h2 className="pantheon-h2">Unified Agentic Ad Infrastructure</h2>
        <p className="pantheon-lead pantheon-lead--center">
          Every capability of the platform maps back to an intelligence in the Pantheon.
        </p>

        <div className="pantheon-infra">
          {/* Decorative connector layer — animated signal/data flow between
              cards. Purely visual: sits behind the cards and never intercepts
              pointer input. */}
          <div className="pantheon-infra-signals" aria-hidden="true" />
          <div className="pantheon-cards">
            {INFRASTRUCTURE.map((cap, i) => (
              <article
                key={cap.id}
                className="pantheon-card"
                style={{
                  "--step": i,
                  "--agent-color": AGENT_COLOR_BY_NAME[cap.agent],
                }}
                data-testid={`infra-card-${cap.id}`}
              >
                <h3 className="pantheon-card-title">{cap.label}</h3>
                <p className="pantheon-card-note">{cap.note}</p>
                <span className="pantheon-card-agent">{cap.agent}</span>
              </article>
            ))}
          </div>
        </div>
      </Section>

      {/* Section 6 — Why Governance Matters */}
      <Section id="pantheon-why" testId="pantheon-why" className="pantheon-section--split">
        <div className="pantheon-split-col">
          <p className="pantheon-eyebrow">The Differentiator</p>
          <h2 className="pantheon-h2">Most AI acts first and governs later</h2>
          <p className="pantheon-lead">
            Pantheon inverts the model. Identity, governance, trust, and certification
            come <strong>before</strong> execution — not after the fact.
          </p>
        </div>
        <div className="pantheon-split-col">
          <div className="pantheon-contrast">
            <div className="pantheon-contrast-row pantheon-contrast-row--them">
              <span className="pantheon-contrast-tag">Most AI systems</span>
              <span>Act first. Govern later.</span>
            </div>
            <div className="pantheon-contrast-row pantheon-contrast-row--us">
              <span className="pantheon-contrast-tag">Pantheon</span>
              <span>Identity → Governance → Trust → Certification → Authority</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Section 7 — Pantheon Observatory */}
      <Section id="pantheon-observatory" testId="pantheon-observatory">
        <p className="pantheon-eyebrow">Visibility</p>
        <h2 className="pantheon-h2">Pantheon Observatory</h2>
        <p className="pantheon-lead pantheon-lead--center">
          A conceptual window into agent activity, trust, certification, governance,
          and readiness — so the network is never a black box.
        </p>

        <div className="pantheon-observatory" aria-hidden="true">
          {[
            { label: "Agent Activity", accent: "violet" },
            { label: "Trust", accent: "cyan" },
            { label: "Certification", accent: "blue" },
            { label: "Governance", accent: "magenta" },
            { label: "Readiness", accent: "teal" },
          ].map((panel, i) => (
            <div
              key={panel.label}
              className={`pantheon-observatory-panel pantheon-observatory-panel--${panel.accent}`}
              style={{ "--step": i }}
            >
              <span className="pantheon-observatory-title">{panel.label}</span>
              <span className="pantheon-observatory-bars">
                <i style={{ "--h": "60%" }} />
                <i style={{ "--h": "85%" }} />
                <i style={{ "--h": "45%" }} />
                <i style={{ "--h": "72%" }} />
              </span>
            </div>
          ))}
        </div>
      </Section>

      {/* Section 8 — The Future */}
      <Section id="pantheon-future" testId="pantheon-future" className="pantheon-section--narrow">
        <p className="pantheon-eyebrow">The Future</p>
        <h2 className="pantheon-h2">Governed Autonomous Operations</h2>
        <div className="pantheon-tenets">
          <p>Authority is earned.</p>
          <p>Trust is measured.</p>
          <p>Certification is required.</p>
          <p>Autonomy is governed.</p>
        </div>
        <p className="pantheon-footnote">
          Forward-looking. Pantheon does not claim autonomous execution today — autonomy
          is the destination of a governed path, not the entry point.
        </p>
      </Section>

      {/* Section 9 — CTA */}
      <Section id="pantheon-cta" testId="pantheon-cta" className="pantheon-section--cta">
        <h2 className="pantheon-h2 pantheon-h2--xl">Unified Agentic Ad Infrastructure</h2>
        <div className="pantheon-cta-row">
          <a
            className="pantheon-cta pantheon-cta--primary"
            href="https://api.cphere.ai/cphere-booking"
            target="_blank"
            rel="noopener noreferrer"
          >
            BUILD A MEDIA PLAN
          </a>
          <a
            className="pantheon-cta pantheon-cta--ghost"
            href="https://www.cphere.ai/platform-access"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn More
          </a>
          <a
            className="pantheon-cta pantheon-cta--ghost"
            href="https://api.cphere.ai/cphere-booking"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book Consultation
          </a>
        </div>
        <p className="pantheon-cta-foot">Cphere Pantheon · The governed intelligence network</p>
      </Section>
    </main>
  );
}
